/**
 * Calculator entry point. The engine runs HERE, in the browser — no network
 * call while answering or working out the result (§9.1), verifiable in
 * DevTools and enforced by a Playwright test. The only requests this page
 * makes are the ones the user asks for by pressing "Send it".
 */
import { determine, savingsRuleForStage } from "@verity/engine";
import type { Determination, OutOfScopeFlag } from "@verity/engine";
import type { Ruleset } from "@verity/rules";
import rulesetJson from "../../../packages/rules/data/appendix-fm.2026-04-11.json";
import { outOfScopeGate, toCaseFacts, type WizardAnswers, type WizardStage } from "@verity/intake";
import { renderDetermination, renderOutOfScope } from "./render.js";
import {
  loadSignedInVisit,
  prefill,
  saveRevision,
  type SignedInVisit,
} from "./revisit.js";
import { track } from "./analytics.js";
import { Wizard } from "./wizard.js";
import { CONSENT_WORDINGS } from "./consent.js";
import { remember } from "@verity/handoff";

const ruleset = rulesetJson as unknown as Ruleset;

const form = document.getElementById("calculator-form") as HTMLFormElement;

/**
 * Who is taking this check, if they are signed in at all (F5/F6).
 *
 * Null for every anonymous visitor, which is almost everybody — the check is a
 * public page and stays one. Everything gated on this is additive. Declared up
 * here because the prefill runs partway down the file and a `let` is not
 * hoisted the way a `function` is.
 */
let visit: SignedInVisit | null = null;
const result = document.getElementById("result") as HTMLElement;

function todayISO(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const field = (name: string): string =>
  (new FormData(form).get(name) as string | null) ?? "";

/** All checked values for a multi-select (the benefit chips). */
const fields = (name: string): string[] =>
  (new FormData(form).getAll(name) as string[]).filter((v) => v !== "");

function numberField(name: string): number | null {
  const raw = field(name).replace(/[£,\s]/g, "");
  if (raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/** "unsure" is a real answer meaning unknown — it must map to null, not false. */
function yesNo(name: string): boolean | null {
  const raw = field(name);
  if (raw === "" || raw === "unsure") return null;
  return raw === "yes";
}

function readAnswers(): WizardAnswers {
  const stage = (field("stage") || "EC") as WizardStage;
  const inCountry = stage === "LTR" || stage === "ILR";
  return {
    sponsor_status: (field("sponsor_status") || null) as WizardAnswers["sponsor_status"],
    /**
     * Derived from the chips, and deliberately only a BOOLEAN. Which benefit
     * someone receives is health information — special category data under
     * UK GDPR Article 9 — and §9.3 relies on this stage holding no such data
     * for the short-form DPIA to be sufficient. The chips exist so the person
     * can recognise their own benefit and answer accurately; the specific
     * benefit is used to derive the answer and then discarded, never stored
     * or transmitted (ADR-0023).
     */
    sponsor_receives_benefit: benefitAnswer(),

    stage,
    intended_date: stage === "EC" || stage === "FIANCE" ? field("intended_date") || null : null,
    first_applied_before_cutoff: inCountry ? yesNo("first_before_cutoff") : null,
    granted_on_first_application: inCountry ? yesNo("granted_first") : null,
    holds_five_year_route_permission: inCountry ? yesNo("holds_permission") : null,
    applying_with_same_partner: inCountry ? yesNo("same_partner") : null,

    relationship_type: (field("relationship_type") ||
      "marriage") as WizardAnswers["relationship_type"],
    date_of_marriage: field("date_of_marriage") || null,
    marriage_legally_valid: yesNo("marriage_valid"),
    cohabiting_since: field("cohabiting_since") || null,
    both_over_18: yesNo("both_over_18"),
    met_in_person: yesNo("met_in_person"),
    within_prohibited_degrees: yesNo("prohibited_degrees"),
    previous_relationships_ended: yesNo("previous_ended"),

    route: (field("route") || "employment") as WizardAnswers["route"],
    income_from: (field("income_from") || null) as WizardAnswers["income_from"],
    months_with_employer: numberField("months_with_employer"),
    gross_annual: numberField("gross_annual"),
    savings_amount: numberField("savings_amount"),
    savings_held_since: field("savings_held_since") || null,

    english_exemption: (field("english_exemption") ||
      "none") as WizardAnswers["english_exemption"],
    english_level: (field("english_level") || null) as WizardAnswers["english_level"],

    legal_right_to_occupy: yesNo("right_to_occupy"),
    without_public_funds: yesNo("without_public_funds"),

    life_in_uk_passed: yesNo("life_in_uk"),

    dependent_children: field("dependent_children") === "yes",
  };
}

/** null while nothing is selected; false only when "None of these" is chosen. */
function benefitAnswer(): boolean | null {
  const selected = fields("sponsor_benefit");
  if (selected.length === 0) return null;
  if (selected.includes("none")) return false;
  return true;
}

/**
 * "None of these" and a named benefit are mutually exclusive, so selecting
 * either clears the other. Done here rather than with radios because the
 * person may receive more than one benefit and should be able to say so.
 */
function wireBenefitChips(): void {
  const chips = [...form.querySelectorAll<HTMLInputElement>('input[name="sponsor_benefit"]')];
  for (const chip of chips) {
    chip.addEventListener("change", () => {
      if (!chip.checked) return;
      if (chip.value === "none") {
        for (const other of chips) if (other.value !== "none") other.checked = false;
      } else {
        const none = chips.find((c) => c.value === "none");
        if (none) none.checked = false;
      }
    });
  }
}
wireBenefitChips();

/**
 * The figure a rule asks for, written onto the option that changes it.
 *
 * Every value here is read from the ruleset — which traces each figure to the
 * register — rather than typed into the markup, so a rules change moves these
 * labels too. Annotations live in a `.option-note` span so they are visible
 * guidance but never mistaken for part of the answer given.
 */
function figure(id: string): string {
  const value = ruleset.figures[id]?.value;
  if (typeof value === "number") {
    return id.includes("multiplier") || id.includes("months") || id.includes("years")
      ? String(value)
      : new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: "GBP",
          maximumFractionDigits: 0,
        }).format(value);
  }
  return String(value ?? "");
}

function annotate(selector: string, text: string): void {
  const input = form.querySelector<HTMLInputElement>(selector);
  const label = input?.closest("label");
  if (!label) return;
  let note = label.querySelector<HTMLElement>(".option-note");
  if (!note) {
    note = document.createElement("span");
    note.className = "option-note";
    label.append(note);
  }
  note.textContent = text;
}

function annotateOptions(): void {
  const stage = field("stage") || "EC";
  const standard = figure("mir_standard");
  const transitional = figure("mir_transitional");
  const floor = figure("savings_floor");
  const floorValue = Number(ruleset.figures["savings_floor"]?.value ?? 0);
  const multiplier = Number(
    ruleset.figures[stage === "ILR" ? "savings_multiplier_ilr" : "savings_multiplier_ec_ltr"]?.value ?? 1,
  );
  const savingsRule = (s: string): string =>
    savingsRuleForStage(
      s,
      floorValue,
      Number(
        ruleset.figures[s === "ILR" ? "savings_multiplier_ilr" : "savings_multiplier_ec_ltr"]?.value ?? 1,
      ),
    );

  // Stage — the savings rule differs, and it is the thing most tools get
  // wrong, so it is stated on the option that decides it.
  annotate('input[name="stage"][value="EC"]', `${standard} income · ${savingsRule("EC")}`);
  annotate(
    'input[name="stage"][value="LTR"]',
    `${standard} income, or ${transitional} for some applications begun before 11 April 2024 · ${savingsRule("LTR")}`,
  );
  annotate(
    'input[name="stage"][value="ILR"]',
    `${standard} income, or ${transitional} transitional · ${savingsRule("ILR")}`,
  );

  annotate(
    'input[name="stage"][value="FIANCE"]',
    `${standard} income · ${savingsRule("EC")} · no accommodation-sharing question at this stage`,
  );

  // Income route — what the rules ask for on each, at the chosen stage.
  const threshold = stage === "EC" ? standard : `${standard}, or ${transitional} if transitional`;
  annotate('input[name="route"][value="employment"]', `Gross salary of ${threshold}`);
  annotate(
    'input[name="route"][value="savings"]',
    stage === "ILR"
      ? `${floor} plus the whole income figure`
      : `${floor} plus ${multiplier}× the income figure`,
  );
  annotate('input[name="route"][value="combination"]', "Savings cover the part the salary does not");
  annotate('input[name="route"][value="other"]', "Not covered — we will say so and point you on");

  // English — mark the level the rules ask for at this stage.
  const requiredLevel =
    stage === "ILR" ? figure("english_level_ilr") : stage === "LTR" ? figure("english_level_ltr") : figure("english_level_ec");
  for (const level of ["none", "A1", "A2", "B1", "B2"]) {
    annotate(
      `input[name="english_level"][value="${level}"]`,
      level === requiredLevel ? "The level asked for at this stage" : "",
    );
  }

  // Relationship — the cohabitation period, from the ruleset.
  annotate(
    'input[name="relationship_type"][value="unmarried_partner"]',
    `At least ${figure("cohabitation_years_unmarried")} years living together`,
  );
}

/** Conditional blocks, refreshed on entering a step and on any change. */
function refreshConditionals(): void {
  const stage = field("stage") || "EC";
  const route = field("route");
  const type = field("relationship_type");
  const exempt = field("english_exemption");
  const inCountry = stage === "LTR" || stage === "ILR";

  toggle("intended-date-question", stage === "EC" || stage === "FIANCE");
  toggle("transitional-questions", inCountry);
  toggle("transitional-detail", inCountry && field("first_before_cutoff") === "yes");

  toggle("marriage-date-question", type !== "unmarried_partner");
  toggle("cohabiting-question", type === "unmarried_partner");

  toggle("employment-questions", route === "employment" || route === "combination");
  toggle("savings-questions", route === "savings" || route === "combination");
  toggle("whose-income-question", route === "employment" || route === "combination");

  toggle("english-level-question", exempt === "none");

  // The level asked for depends on the stage — say so before they answer.
  const hint = document.getElementById("english-stage-hint");
  if (hint) {
    hint.textContent =
      stage === "ILR"
        ? "At settlement the rules ask for B1, rising to B2 for applications made on or after 26 March 2027."
        : stage === "LTR"
          ? "At an extension the rules ask for A2 in speaking and listening — a higher level than at a first application, and A1 does not carry over."
          : "At a first application the rules ask for A1 in speaking and listening.";
  }

  annotateOptions();

  // Whose income can count is stage-dependent (FIN.WHOSE-INCOME).
  const whose = document.getElementById("whose-hint");
  if (whose) {
    whose.textContent =
      stage === "EC" || stage === "FIANCE"
        ? "At a first application from outside the UK, only your partner's income in the UK can count towards the requirement."
        : "In-country, income from your own permitted employment can count alongside your partner's.";
  }
}

function toggle(id: string, show: boolean): void {
  const section = document.getElementById(id);
  if (section) section.hidden = !show;
}

const REQUIRED: Record<number, () => Array<{ name: string; message: string }>> = {
  1: () => [
    { name: "sponsor_status", message: "Please choose the option that describes your partner." },
    {
      name: "sponsor_benefit",
      message: 'Please select any benefits your partner receives, or "None of these".',
    },
  ],
  2: () => {
    const stage = field("stage");
    if (!stage) {
      return [{ name: "stage", message: "Please choose which application this is." }];
    }
    if (stage !== "LTR" && stage !== "ILR") return [];
    const required = [
      {
        name: "first_before_cutoff",
        message: "Please say whether your first application was made before 11 April 2024.",
      },
    ];
    if (field("first_before_cutoff") === "yes") {
      required.push(
        { name: "granted_first", message: "Please say whether that application led to a grant." },
        {
          name: "holds_permission",
          message: "Please say whether you currently hold partner permission on the five-year route.",
        },
        {
          name: "same_partner",
          message: "Please say whether this application is to stay with the same partner.",
        },
      );
    }
    return required;
  },
  3: () => {
    const type = field("relationship_type");
    if (!type) {
      return [{ name: "relationship_type", message: "Please choose how you would describe your relationship." }];
    }
    const required = [
      { name: "both_over_18", message: "Please say whether both of you are aged 18 or over." },
      { name: "met_in_person", message: "Please say whether you have met in person." },
      { name: "prohibited_degrees", message: "Please say whether you are related as close family." },
      {
        name: "previous_ended",
        message: "Please say whether any earlier relationship has ended permanently.",
      },
    ];
    if (type === "unmarried_partner") {
      required.unshift({
        name: "cohabiting_since",
        message: "Please enter the date you started living together.",
      });
    } else {
      required.unshift(
        { name: "date_of_marriage", message: "Please enter the date of the marriage or civil partnership." },
        { name: "marriage_valid", message: "Please say whether it is recognised as valid where it took place." },
      );
    }
    return required;
  },
  4: () => {
    const route = field("route");
    if (!route) {
      return [{ name: "route", message: "Please choose how the financial requirement will be met." }];
    }
    if (route === "other") return [];
    const required: Array<{ name: string; message: string }> = [];
    if (route === "employment" || route === "combination") {
      required.push(
        { name: "income_from", message: "Please say whose income it is." },
        {
          name: "months_with_employer",
          message: "Please enter how many months have been spent in the current job.",
        },
        { name: "gross_annual", message: "Please enter the gross annual salary." },
      );
    }
    if (route === "savings" || route === "combination") {
      required.push(
        { name: "savings_amount", message: "Please enter the amount of savings held." },
        {
          name: "savings_held_since",
          message: "Please enter the date the savings have been held at that level since.",
        },
      );
    }
    return required;
  },
  5: () => {
    const exemption = field("english_exemption");
    if (!exemption) {
      return [{ name: "english_exemption", message: "Please say whether any of these apply." }];
    }
    return exemption === "none"
      ? [{ name: "english_level", message: "Please choose the highest English test level held." }]
      : [];
  },
  6: () => [
    { name: "right_to_occupy", message: "Please answer about the right to occupy the home." },
    { name: "without_public_funds", message: "Please answer about support without public funds." },
  ],
  7: () =>
    (field("stage") || "EC") === "ILR"
      ? [{ name: "life_in_uk", message: "Please say whether the Life in the UK test has been passed." }]
      : [],
  8: () => [
    { name: "dependent_children", message: "Please say whether any dependent children are included." },
  ],
};

const wizard = new Wizard(form, {
  onStepChange: (step) => {
    refreshConditionals();
    if (step === RESULTS_STEP && result.childElementCount === 0) computeAndRender();
    // The wizard shows a submit button on the last step. There is nothing to
    // submit — the result is worked out in the browser — so it never appears.
    const submit = document.getElementById("submit-button");
    if (submit) (submit as HTMLButtonElement).hidden = true;
    // On the question before the result, say what Continue will do.
    const next = document.getElementById("next-button");
    if (next) next.textContent = step === RESULTS_STEP - 1 ? "See what the rules ask for" : "Continue";
  },
  requiredFor: (step) => REQUIRED[step]?.() ?? [],
  // Life in the UK applies only at settlement. Showing the step with
  // "nothing to answer here" wastes a click and makes people wonder what
  // they missed, so it is skipped and the numbering closes up behind it.
  isActive: (step) => (step === 7 ? (field("stage") || "EC") === "ILR" : true),
});

/**
 * The check starts behind a button.
 *
 * Hidden HERE rather than with a `hidden` attribute in the markup: without
 * JavaScript the calculator cannot work at all, but it can still be READ, and
 * a page that renders a button which does nothing is worse than one that
 * renders the questions. So the default document shows everything and this
 * takes it away only once there is something to take it away with.
 */
/**
 * The address asked for on the intro, if one was given.
 *
 * Optional by design: the result renders on screen regardless, and an address
 * only adds a copy by email. Held in a variable rather than read from the DOM
 * at send time because the intro is hidden by then.
 */
const capture: { firstName: string; email: string; marketing: boolean } = {
  firstName: "",
  email: "",
  marketing: false,
};

const intro = document.getElementById("intro");
const shell = document.querySelector<HTMLElement>(".check-shell");
const startButton = document.getElementById("start-button");

if (intro && shell && startButton) {
  shell.hidden = true;

  // The wording someone agreed to has to be on screen when they agree to it.
  const consentLine = document.getElementById("intro-consent");
  if (consentLine) consentLine.textContent = CONSENT_WORDINGS["result-by-email-v1"] ?? "";

  startButton.addEventListener("click", () => {
    const emailField = document.getElementById("intro-email") as HTMLInputElement | null;
    const nameField = document.getElementById("intro-first-name") as HTMLInputElement | null;
    const marketingBox = document.getElementById("intro-marketing") as HTMLInputElement | null;
    const emailError = document.getElementById("err-intro-email");
    const typed = (emailField?.value ?? "").trim();

    // Empty is fine — this is optional. Something that is not an address is
    // not: letting it through would mean promising a copy and never sending
    // one, which is worse than not offering.
    if (typed !== "" && !emailField?.checkValidity()) {
      if (emailError) {
        emailError.textContent = "That does not look like an email address. Correct it, or clear it and carry on without a copy.";
        emailError.hidden = false;
      }
      emailField?.focus();
      return;
    }
    if (emailError) emailError.hidden = true;

    capture.firstName = (nameField?.value ?? "").trim();
    capture.email = typed;
    capture.marketing = marketingBox?.checked ?? false;

    intro.hidden = true;
    shell.hidden = false;
    // Hand off to the wizard's own focus handling so a screen reader hears the
    // first question rather than being left where the button used to be.
    wizard.show(1, { moveFocus: true });
    track("calculator_started");
  });
}

form.addEventListener("change", (event) => {
  if (isFromResult(event)) return;
  // An answer changed, so whatever is on the result step is now out of date.
  // Clearing it means arriving there recomputes rather than showing a figure
  // worked out from answers that have since been edited.
  result.replaceChildren();
  refreshConditionals();
  // An answer here can change how many steps there are, so the count and the
  // rail are redrawn immediately rather than at the next navigation.
  wizard.refresh();
});
refreshConditionals();

/**
 * A gate answered mid-wizard is shown immediately rather than after five more
 * steps of questions we would not use.
 */
/**
 * A gate answered mid-wizard skips the questions we would not use — but on
 * Continue, not the instant the radio changes. Jumping the moment an option is
 * selected pulls the reader off a step they are still filling in: the benefit
 * chips sit on the same step as the status that gates them, and answering the
 * first would have thrown them out before they reached the second.
 */
form.addEventListener("change", (event) => {
  if (isFromResult(event)) return;
  const target = event.target as HTMLInputElement;
  if (!["sponsor_status", "sponsor_benefit", "stage", "route"].includes(target.name)) return;
  pendingGate = outOfScopeGate(readAnswers());
});

let pendingGate: string | null = null;

// Continue, when a gate is set, goes straight to the result rather than
// through questions whose answers would not be used. Capture phase so it runs
// before the wizard's own handler advances one step.
document.getElementById("next-button")?.addEventListener(
  "click",
  (event) => {
    if (!pendingGate || wizard.currentStep() === RESULTS_STEP) return;
    if (!wizard.validate()) return;
    event.stopImmediatePropagation();
    wizard.show(RESULTS_STEP);
  },
  true,
);

/**
 * "Start your application" (F1).
 *
 * Stashes the answers on this device and lets the link navigate. Nothing is
 * sent: someone who has pressed a button has not agreed to give us their data,
 * and §9.2 says nothing leaves the browser until they choose it. The account
 * they are about to create is what turns the stash into a case (F2).
 *
 * Delegated from the result container because the result is re-rendered
 * whenever an answer changes, so a listener bound to the button itself would be
 * attached to an element that no longer exists.
 */
function wireStartApplication(): void {
  result.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement | null)?.closest("#start-application");
    if (!target || !lastAnswers) return;
    remember({
      answers: lastAnswers as unknown as Record<string, unknown>,
      saved_at: todayISO(),
      // `exactOptionalPropertyTypes` is on: an absent stage means omitting the
      // key, not setting it to undefined.
      ...(lastFacts ? { stage: lastFacts.stage } : {}),
    });
    track("application_started", { stage: lastFacts?.stage ?? "" });
  });
}
wireStartApplication();
wireSaveRevision();

/**
 * Save the revised answers as a new version, then go back to the case.
 *
 * Delegated from the result container for the same reason as the offer button:
 * the result is re-rendered whenever an answer changes, so a listener bound to
 * the button itself would be attached to an element that no longer exists.
 */
function wireSaveRevision(): void {
  result.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement | null)?.closest("#save-revision");
    if (!target || !lastAnswers) return;

    const button = target as HTMLButtonElement;
    button.disabled = true;
    button.textContent = "Saving\u2026";

    void (async () => {
      const saved = await saveRevision(lastAnswers as WizardAnswers);
      if (!saved) {
        button.disabled = false;
        button.textContent = "That did not save — try again";
        return;
      }
      track("case_revised");
      window.location.assign("/app/");
    })();
  });
}

/**
 * If this browser has a case, put its answers back in the form (F5).
 *
 * Runs after the form is wired so the `change` events `prefill` dispatches
 * reach the conditional logic — setting `stage` without one leaves the
 * in-country questions hidden and their answers unreachable.
 *
 * Silent for anonymous visitors, which is the overwhelming majority: a failed
 * request means the ordinary blank check and nothing else.
 */
void (async () => {
  visit = await loadSignedInVisit();
  if (!visit) return;

  // 1 · THE WAY BACK. Sending somebody from the app to a page with no route
  // home is how navigation gets lost, which is exactly what happened.
  const backWrap = document.getElementById("check-backlink-wrap");
  const back = document.getElementById("check-backlink");
  if (backWrap && back) {
    back.textContent = visit.context === "setup" ? "Back to set-up" : "Back to your case";
    backWrap.hidden = false;
  }

  // 2 · WE ALREADY HAVE THEIR NAME AND ADDRESS. Asking a signed-up customer
  // for them again is the clearest possible sign that this page does not know
  // who they are. The capture is for strangers.
  const capture = document.querySelector<HTMLElement>(".intro-capture");
  if (capture) capture.hidden = true;

  const startLabel = document.getElementById("start-button");
  if (startLabel) {
    startLabel.textContent = visit.context === "setup" ? "Start the check" : "Change your answers";
  }

  if (!visit.existing) {
    // The F6 path: signed up without checking first. Nothing to prefill, and
    // the check is simply the next step in setting up.
    const banner = document.getElementById("revisit-banner");
    if (banner) {
      banner.textContent =
        "This is the last part of setting up. Everything on your case is worked out from these answers.";
      banner.hidden = false;
    }
    return;
  }

  // 3 · THEIR ANSWERS, BACK IN THE BOXES.
  const { benefitNeedsReasking } = prefill(form, visit.existing.answers);
  refreshConditionals();
  wizard.refresh();

  const banner = document.getElementById("revisit-banner");
  if (!banner) return;
  banner.textContent =
    "We have filled these in from your case. Change whatever is different and save at the end.";
  if (benefitNeedsReasking) {
    // See revisit.ts. We hold a boolean and never the benefit itself, so this
    // cannot be restored — and saying so is better than a silently blank
    // question somebody has already answered once (ADR-0023).
    banner.textContent +=
      " One exception: you told us your partner receives one of the listed benefits, but we do not record which one — so please pick it again.";
  }
  banner.hidden = false;
})();

function showOutOfScope(flag: string): void {
  const referral = ruleset.out_of_scope[flag];
  if (!referral) return;
  // The wizard gates before the engine runs, so the flag is passed explicitly
  // here — the same one `determine()` would have reported.
  renderOutOfScope(result, [referral.reason], referral.referral_hint, [
    flag as OutOfScopeFlag,
  ]);
  track("calculator_out_of_scope", { reason: flag });
}

let lastDetermination: Determination | null = null;
let lastAnswers: WizardAnswers | null = null;
let lastFacts: ReturnType<typeof toCaseFacts> | null = null;

/**
 * The questions and answers exactly as they appeared on screen, harvested
 * from the DOM. Read from the rendered labels rather than rebuilt from a
 * second table of strings, so the email shows the same words the person did —
 * the same reason the consent wordings have a parity test.
 */
function answersShown(): Array<{ section: string; question: string; answer: string }> {
  const shown: Array<{ section: string; question: string; answer: string }> = [];
  const clean = (t: string) => t.replace(/\s+/g, " ").trim().slice(0, 300);

  /**
   * Hidden because a conditional block does not apply — as opposed to hidden
   * because it is not the step currently on screen. Every completed step is
   * hidden, so a plain `closest("[hidden]")` would harvest only the last one.
   */
  const hiddenWithinStep = (node: HTMLElement, step: HTMLElement): boolean => {
    for (let el: HTMLElement | null = node; el && el !== step; el = el.parentElement) {
      if (el.hidden) return true;
    }
    return false;
  };

  for (const step of form.querySelectorAll<HTMLElement>(".step")) {
    const section = clean(step.querySelector("h2")?.textContent ?? "");

    // Radio and checkbox groups: the legend or the group label is the
    // question, the checked labels are the answer.
    for (const group of step.querySelectorAll<HTMLElement>("fieldset, .yesno")) {
      if (hiddenWithinStep(group, step)) continue;
      const question = clean(
        group.querySelector("legend")?.textContent ??
          group.querySelector("span[id]")?.textContent ??
          "",
      );
      const chosen = [...group.querySelectorAll<HTMLInputElement>("input:checked")]
        .map((input) => {
          const label = input.closest("label");
          // Where an option has a name and a longer description (the English
          // levels), record the name — the description is guidance, not the
          // answer they gave.
          const name = label?.querySelector(".level-name")?.textContent;
          if (name) return clean(name);
          // The inline threshold note is guidance we added, not something the
          // person told us, so it must not appear as their answer.
          const copy = label?.cloneNode(true) as HTMLElement | undefined;
          copy?.querySelectorAll(".option-note").forEach((n) => n.remove());
          return clean(copy?.textContent ?? input.value);
        })
        .filter(Boolean);
      if (question && chosen.length > 0) {
        shown.push({ section, question, answer: chosen.join(", ") });
      }
    }

    // Free-text and date inputs: their own <label> is the question.
    for (const input of step.querySelectorAll<HTMLInputElement>(
      'input[type="text"], input[type="number"], input[type="date"]',
    )) {
      if (hiddenWithinStep(input, step) || input.value.trim() === "") continue;
      const label = step.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
      const question = clean(label?.textContent ?? input.name);
      shown.push({ section, question, answer: clean(input.value) });
    }
  }
  return shown.slice(0, 40);
}

/**
 * Did this event come from the rendered result rather than a question?
 *
 * The result is a step inside the form, so the email panel's inputs bubble to
 * the form's change handlers. Without this guard, typing a name into the panel
 * refreshes the wizard, re-renders the result, and destroys the panel mid-use.
 */
function isFromResult(event: Event): boolean {
  return result.contains(event.target as Node);
}

/** The last step. Reaching it is what produces the result. */
const RESULTS_STEP = 9;

/**
 * Work out the result and render it into the results step.
 *
 * Called when the reader arrives at that step, not when a button is pressed:
 * the wizard owns navigation, and tying the calculation to arrival means Back
 * and Continue behave the same way here as on every other step.
 */
function computeAndRender(): void {
  const answers = readAnswers();
  const gate = outOfScopeGate(answers);
  if (gate) return showOutOfScope(gate);

  const facts = toCaseFacts(answers, todayISO());
  const det = determine(facts, ruleset);
  lastDetermination = det;
  lastAnswers = answers;
  lastFacts = facts;

  renderDetermination(result, det, ruleset, visit?.context, visit?.existing?.version);

  if (det.verdict === "OUT_OF_SCOPE") {
    track("calculator_out_of_scope", { reason: "engine" });
  } else {
    track("calculator_completed", {
      stage: answers.stage,
      route: answers.route,
      verdict: det.verdict,
      category: det.requirements.find((r) => r.rule_id === "FIN.CATEGORY")?.value ?? "",
      threshold_basis: det.requirements.find((r) => r.rule_id === "FIN.THRESHOLD")?.value ?? "",
    });
    wireEmailPanel();
    void autoSendIfRequested();
  }
}

form.addEventListener("submit", (event) => {
  // The submit button is hidden on every step now — the results step is last
  // and has no submit. This stays only so that Enter on the final question
  // cannot post the form to the server.
  event.preventDefault();
});

// ─────────────────────────────── email ────────────────────────────────

type SendMode = "me" | "partner";
let mode: SendMode = "me";

/**
 * Wire the result's buttons ONCE, by delegation from the container.
 *
 * The buttons live inside #result, which is rebuilt from scratch on every
 * render — so a listener attached directly to a button is attached to a node
 * that the next render throws away. That produced a button which existed,
 * looked right, and did nothing. Delegating from the container, which is never
 * replaced, means the wiring cannot go stale and cannot be attached twice.
 */
result.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest("button");
  if (!target) return;
  if (target.id === "email-me-button") openPanel("me");
  else if (target.id === "email-partner-button") openPanel("partner");
  else if (target.id === "email-send-button") void send();
});

/** Per-render state that delegation cannot carry: which buttons to offer. */
function wireEmailPanel(): void {
  const meButton = document.getElementById("email-me-button");
  // Already have an address? The copy is on its way, so offering to send one
  // reads as though the first failed. Hidden rather than removed so the
  // panel's own fields still work for anyone who skipped the intro.
  if (meButton) (meButton as HTMLButtonElement).hidden = capture.email !== "";
}

function openPanel(next: SendMode): void {
  mode = next;
  const panel = document.getElementById("email-panel")!;
  const heading = document.getElementById("email-panel-heading")!;
  const partnerField = document.getElementById("partner-field")!;
  const wording = panel.querySelector(".consent-wording")!;

  panel.hidden = false;
  partnerField.hidden = mode === "me";
  // Do not ask twice for what the intro already collected.
  const ownFields = panel.querySelectorAll<HTMLElement>("#email-first-name, #email-address");
  for (const field of ownFields) {
    const wrap = field.closest(".field");
    if (wrap) (wrap as HTMLElement).hidden = capture.email !== "";
  }
  heading.textContent =
    mode === "me" ? "Email this result to you" : "Email this result to you and your partner";
  wording.textContent =
    CONSENT_WORDINGS[mode === "me" ? "save-result-v1" : "share-partner-v2"] ?? "";
  setStatus("");
  if (mode === "partner") track("share_clicked");
  heading.focus();
}

function setStatus(message: string, tone: "" | "ok" | "attention" = ""): void {
  const status = document.getElementById("email-status");
  if (!status) return;
  status.textContent = message;
  status.className = `email-status${tone ? ` ${tone}` : ""}`;
}

const value = (id: string): string =>
  (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function send(): Promise<void> {
  if (!lastDetermination || !lastAnswers || !lastFacts) return;

  // The applicant's own details come from the intro when they were given
  // there, so the partner panel only has to ask for the one thing it does not
  // already know.
  const firstName = capture.firstName !== "" ? capture.firstName : value("email-first-name");
  const email = capture.email !== "" ? capture.email : value("email-address");
  const partnerEmail = mode === "partner" ? value("email-partner-address") : "";

  if (firstName === "") return setStatus("Please enter your first name.", "attention");
  if (!EMAIL.test(email)) return setStatus("Please enter your email address.", "attention");
  if (mode === "partner" && !EMAIL.test(partnerEmail)) {
    return setStatus("Please enter your partner's email address.", "attention");
  }
  if (mode === "partner" && partnerEmail.toLowerCase() === email.toLowerCase()) {
    return setStatus("That is your own address — use “Email it to me” instead.", "attention");
  }

  const button = document.getElementById("email-send-button") as HTMLButtonElement;
  button.disabled = true;
  setStatus("Sending…");

  const carrier = financialCarrier(lastDetermination);
  const body = {
    email,
    first_name: firstName,
    partner_email: mode === "partner" ? partnerEmail : null,
    inputs: lastAnswers,
    // The facts, so the API can re-run the same engine and build an email
    // that cannot disagree with this screen.
    facts: lastFacts,
    answers_shown: answersShown(),
    result: {
      verdict: lastDetermination.verdict,
      category: lastDetermination.requirements.find((r) => r.rule_id === "FIN.CATEGORY")?.value ?? null,
      headroom: carrier?.figures?.["headroom"] ?? null,
      eligible_from: lastDetermination.not_ready?.eligible_from ?? null,
    },
    ruleset_version: lastDetermination.ruleset_version,
    engine_version: lastDetermination.engine_version,
    consent: {
      marketing:
        capture.email !== ""
          ? capture.marketing
          : ((document.getElementById("email-marketing") as HTMLInputElement | null)?.checked ??
            false),
      wording_version: consentVersion(),
    },
    source: sourceAttribution(),
  };

  try {
    const response = await fetch("/api/saveResult", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.status === 429) {
      setStatus("That is a few too many sends in a minute. Please try again shortly.", "attention");
    } else if (response.status === 400) {
      // The only status that genuinely means "look at what you typed".
      setStatus("That address does not look right. Check it and try again.", "attention");
    } else if (!response.ok) {
      // Anything else is our fault, not the reader's. Telling someone to
      // check their address when the server could not send blames them for
      // something they cannot fix and invites them to retype a correct
      // address repeatedly — which is exactly what a missing send permission
      // on the mail service looked like from the outside.
      setStatus(
        "Something went wrong at our end and the email did not go out. Your result is safe on this page — try again in a moment.",
        "attention",
      );
    } else {
      // ON-6 — keep the save token on this device so that if this person goes
      // on to create an account, their answers come with them instead of being
      // typed a second time. Written only here, on a successful save, which is
      // the only moment the user has chosen to let the result leave the page
      // (§9.2). Failing to store it costs a retype, never the send, so it
      // cannot throw and is not awaited on.
      try {
        const { save_token: saveToken } = (await response.json()) as { save_token?: string };
        if (saveToken) {
          remember({
            token: saveToken,
            saved_at: todayISO(),
            ...(lastFacts ? { stage: lastFacts.stage } : {}),
          });
        }
      } catch {
        /* the copy was sent; the handoff is a convenience on top of that */
      }
      setStatus(
        mode === "me"
          ? "Sent. Check your inbox — it may take a minute to arrive."
          : "Sent to both of you. It may take a minute to arrive.",
        "ok",
      );
    }
  } catch {
    setStatus("That did not send — the connection failed. Please try again.", "attention");
  } finally {
    button.disabled = false;
  }
}

/**
 * Which wording the person actually agreed to.
 *
 * Recording the wrong version is worse than recording none: the point of the
 * version is that the exact sentence someone saw can be reconstructed, and a
 * copy sent because they typed an address on the intro was not agreed under
 * the panel's wording.
 */
function consentVersion(): string {
  // v2 since 25 Aug 2026 (ADR-0048): the address is kept, and the wording says
  // so. v1 stays in CONSENT_WORDINGS because records written under it were
  // consented to a different promise — published wordings are append-only.
  if (mode === "partner") return "share-partner-v2";
  return capture.email !== "" ? "result-by-email-v1" : "save-result-v1";
}

/**
 * Send the result to the address given on the intro, without being asked
 * again. Silent by design — the result is already on screen, so a failure
 * here is worth reporting quietly rather than interrupting someone reading
 * their answer.
 */
async function autoSendIfRequested(): Promise<void> {
  if (capture.email === "" || !lastDetermination) return;
  if (lastDetermination.verdict === "OUT_OF_SCOPE") return;
  mode = "me";
  noteEmail("sending", capture.email);
  await send();
  // send() reports through the panel's status line, which is not on screen
  // here — nobody opened the panel. Read the outcome back from it so the note
  // at the top of the result says what actually happened.
  const status = document.getElementById("email-status");
  const failed = status?.classList.contains("attention") ?? false;
  noteEmail(failed ? "failed" : "sent", capture.email);
}

/**
 * A line at the top of the result saying where the copy went.
 *
 * Someone who gave an address expects an email; saying nothing leaves them
 * wondering whether it worked, and checking an inbox that has not received it
 * yet. It is also the only place a silent failure would otherwise surface —
 * the panel's status line is hidden when nobody opened the panel.
 */
function noteEmail(state: "sending" | "sent" | "failed", address: string): void {
  const card = result.querySelector(".result-card");
  if (!card) return;
  let note = card.querySelector<HTMLElement>(".email-note");
  if (!note) {
    note = document.createElement("p");
    note.className = "email-note";
    note.setAttribute("role", "status");
    card.prepend(note);
  }
  note.classList.toggle("attention", state === "failed");
  note.textContent =
    state === "sending"
      ? `Sending a copy to ${address}…`
      : state === "sent"
        ? `A copy of this is on its way to ${address}. It may take a minute to arrive.`
        : `We could not email a copy to ${address}. Your result is here, and you can try again below.`;
}

/** The requirement carrying the financial verdict, for the headroom figure. */
function financialCarrier(det: Determination) {
  const order = [
    "FIN.COMBINE.EC-LTR",
    "FIN.COMBINE.ILR",
    "FIN.SAVINGS.EC-LTR",
    "FIN.SAVINGS.ILR",
    "FIN.INCOME.CAT-A",
  ];
  for (const id of order) {
    const found = det.requirements.find((r) => r.rule_id === id && r.status !== "NOT_APPLICABLE");
    if (found) return found;
  }
  return null;
}

function sourceAttribution(): { referrer: string; utm: Record<string, string>; shared_from_id: null } {
  const utm: Record<string, string> = {};
  for (const [key, val] of new URLSearchParams(window.location.search)) {
    if (key.startsWith("utm_")) utm[key] = val;
  }
  return { referrer: document.referrer, utm, shared_from_id: null };
}

document.getElementById("result")?.addEventListener(
  "toggle",
  (e) => {
    if ((e.target as HTMLElement).id === "evidence") track("evidence_viewed");
  },
  true,
);
